import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertTriangle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Loader2, RefreshCw
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';

const ISSUE_TYPE_OPTIONS = [
    { value: '', label: 'All Issue Types' },
    { value: 'MISSING_REQUIRED_FIELD', label: 'Missing Required Field' },
    { value: 'POTENTIAL_DUPLICATE', label: 'Potential Duplicate' },
    { value: 'INVALID_PHONE_FORMAT', label: 'Invalid Phone Format' },
    { value: 'STALE_RECORD', label: 'Stale Record' },
    { value: 'NO_PRODUCTS', label: 'No Products' },
    { value: 'INCOMPLETE_KYC', label: 'Incomplete KYC' },
    { value: 'EXPIRED_KYC', label: 'Expired KYC' },
    { value: 'INACTIVE_WITH_ACTIVE_PRODUCTS', label: 'Inactive With Active Products' },
    { value: 'MISSING_KYC_DOCUMENTS', label: 'Missing KYC Documents' },
    { value: 'KYC_CLIENT_FIELD_MISMATCH', label: 'KYC Client Field Mismatch' },
];

const SEVERITY_FILTER_OPTIONS = [
    { value: '', label: 'All Severities' },
    { value: 'HIGH', label: 'High' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'LOW', label: 'Low' },
];

const severityBadge = (severity) => {
    const classes = {
        HIGH: 'bg-red-100 text-red-800',
        MEDIUM: 'bg-yellow-100 text-yellow-800',
        LOW: 'bg-blue-100 text-blue-800',
    };
    return (
        <span className={`px-2 py-1 text-xs rounded-full font-medium ${classes[severity] || 'bg-gray-100 text-gray-800'}`}>
            {severity}
        </span>
    );
};

const IssuesContent = () => {
    const navigate = useNavigate();
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginating, setPaginating] = useState(false);
    const [error, setError] = useState('');
    const [issueTypeFilter, setIssueTypeFilter] = useState('');
    const [severityFilter, setSeverityFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchIssues = useCallback(async () => {
        try {
            setPaginating(true);
            setError('');

            const response = await dataQualityService.issues.getAll(
                currentPage, pageSize,
                issueTypeFilter || undefined
            );

            if (response.success && response.data) {
                const pageData = response.data;
                let content = pageData.content || [];

                // Client-side severity filter
                if (severityFilter) {
                    content = content.filter(i => i.severity === severityFilter);
                }

                setIssues(content);
                setTotalElements(severityFilter ? content.length : (pageData.totalElements || 0));
                setTotalPages(severityFilter ? Math.ceil(content.length / pageSize) : (pageData.totalPages || 0));
            }
        } catch (err) {
            console.error('Error fetching issues:', err);
            setError('Failed to load data quality issues');
        } finally {
            setLoading(false);
            setPaginating(false);
        }
    }, [currentPage, pageSize, issueTypeFilter, severityFilter]);

    useEffect(() => {
        fetchIssues();
    }, [fetchIssues]);

    useEffect(() => {
        setCurrentPage(0);
    }, [issueTypeFilter, severityFilter]);

    const formatIssueType = (type) => {
        if (!type) return 'N/A';
        return type.replace(/_/g, ' ').toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (loading) {
        return <LoadingSpinner message="Scanning for data quality issues..." />;
    }

    const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
    const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Data Quality Issues</h2>
                    <p className="text-gray-600 mt-1">{totalElements.toLocaleString()} issues found</p>
                </div>
                <button
                    onClick={fetchIssues}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Rescan
                </button>
            </div>

            {error && <ErrorAlert error={error} onRetry={fetchIssues} />}

            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Issue Type</label>
                        <CustomSelect
                            value={issueTypeFilter}
                            onChange={setIssueTypeFilter}
                            options={ISSUE_TYPE_OPTIONS}
                            placeholder="All Issue Types"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                        <CustomSelect
                            value={severityFilter}
                            onChange={setSeverityFilter}
                            options={SEVERITY_FILTER_OPTIONS}
                            placeholder="All Severities"
                        />
                    </div>
                </div>
            </div>

            {/* Issues Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
                {paginating && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                {issues.length === 0 ? (
                    <div className="text-center py-12">
                        <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No issues found</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affected Field</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suggested Fix</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {issues.map((issue, idx) => (
                                <tr key={`${issue.clientId}-${issue.issueType}-${idx}`} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm font-medium text-gray-900">{formatIssueType(issue.issueType)}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {severityBadge(issue.severity)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => navigate(`/clients/${issue.clientId}`)}
                                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                        >
                                            {issue.clientName}
                                        </button>
                                        <p className="text-xs text-gray-400">{issue.clientCode}</p>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                        {issue.description}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {issue.affectedField || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                        {issue.suggestedFix || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Pagination */}
                {(totalPages > 0 || totalElements > 0) && (
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                                <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                                <span className="font-medium">{totalElements.toLocaleString()}</span> results
                            </span>
                            <CustomSelect
                                value={pageSize}
                                onChange={(val) => { setPageSize(Number(val)); setCurrentPage(0); }}
                                options={[
                                    { value: 10, label: '10 per page' },
                                    { value: 20, label: '20 per page' },
                                    { value: 50, label: '50 per page' },
                                ]}
                                dropUp
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronsLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                                Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                            </span>
                            <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                                <ChevronsRight className="w-5 h-5" />
                            </button>
                            {paginating && <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-2" />}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IssuesContent;
